import { useRef } from 'react';

const MAX_FOCUS_ATTEMPTS = 3;
const FOCUS_VERIFICATION_DELAY_MS = 10;
const EXPONENTIAL_BACKOFF_BASE_MS = 50;

// Helper function for exponential backoff calculation
const calculateBackoffDelay = (attempt: number): number => {
  return Math.pow(2, attempt) * EXPONENTIAL_BACKOFF_BASE_MS;
};

export const useFocusRestoration = (): {
  storeFocus: () => void;
  restoreFocus: () => void;
  clearStoredFocus: () => void;
} => {
  const lastActiveElement = useRef<HTMLElement | null>(null);
  const storeFocus = () => {
    const activeElement = document.activeElement as HTMLElement;
    // Only store if it's a focusable element and not the body
    if (
      activeElement &&
      activeElement !== document.body &&
      typeof activeElement.focus === 'function'
    ) {
      lastActiveElement.current = activeElement;
    }
  };

  const clearStoredFocus = () => {
    lastActiveElement.current = null;
  };

  const isElementFocusable = (element: HTMLElement): boolean => {
    return (
      document.contains(element) &&
      !element.hidden &&
      !element.hasAttribute('disabled') &&
      element.tabIndex !== -1 &&
      typeof element.focus === 'function'
    );
  };

  const restoreFocus = () => {
    if (!lastActiveElement.current) {
      return;
    }

    const elementToFocus = lastActiveElement.current;

    // Multiple timing strategies to ensure focus restoration
    const attemptFocus = (attempt: number = 0) => {
      if (attempt > MAX_FOCUS_ATTEMPTS) {
        lastActiveElement.current = null;
        return;
      }

      if (!isElementFocusable(elementToFocus)) {
        lastActiveElement.current = null;
        return;
      }

      try {
        elementToFocus.focus();

        setTimeout(() => {
          if (document.activeElement === elementToFocus) {
            lastActiveElement.current = null;
          } else if (attempt < MAX_FOCUS_ATTEMPTS) {
            // Focus didn't take, try again with exponential backoff
            setTimeout(() => attemptFocus(attempt + 1), calculateBackoffDelay(attempt));
          }
        }, FOCUS_VERIFICATION_DELAY_MS);
      } catch {
        if (attempt < MAX_FOCUS_ATTEMPTS) {
          // exponential backoff
          setTimeout(() => attemptFocus(attempt + 1), calculateBackoffDelay(attempt));
        } else {
          lastActiveElement.current = null;
        }
      }
    };

    requestAnimationFrame(() => {
      attemptFocus(0);
    });
  };

  return {
    storeFocus,
    restoreFocus,
    clearStoredFocus,
  };
};
